import { classifyHttpError, extractBackendErrorCode } from '@/core/api/http-errors';
import type { ErrorMap, HttpMethod } from '@/app/error-registry';

/**
 * Resolves an unknown error thrown by an AI endpoint into an i18n title key.
 *
 * Takes the map rather than the URL, and imports `ErrorMap` as a **type only**,
 * so this module never pulls `@/app/error-registry` into the runtime graph.
 * That matters more than it looks: the registry imports every feature's
 * `public.ts`, so a value import here made `features/ai/public` transitively
 * load all 27 features. Three unrelated integration suites failed on mocked
 * feature modules that suddenly had to export their error maps.
 *
 * The caller always knows which endpoint it just called, so URL pattern
 * matching bought nothing here. Registry precedence is still covered by
 * `errors.spec.ts`, which tests the registry directly.
 */
export function resolveAiError(map: ErrorMap, method: HttpMethod, error: unknown): string {
  const errorType = classifyHttpError(error);

  // Network and timeout are global: no feature context makes them clearer.
  if (errorType === 'network' || errorType === 'timeout') {
    return `core:errors.${errorType}.title`;
  }

  const errorCode = extractBackendErrorCode(error);
  const methodMap = map[method];

  return (
    (errorCode ? methodMap?.[errorCode] : undefined) ??
    methodMap?.[errorType] ??
    methodMap?.default ??
    `core:errors.${errorType}.title`
  );
}
